/**
 * Keyboard shortcuts for next / previous inside an open detail modal
 * (kanban 97abd058). j / ArrowRight = next, k / ArrowLeft = previous.
 *
 * Nothing fires while the person is typing in an input, textarea, select or
 * content-editable area, when a modifier is held, or when `enabled` is false
 * (the modal passes false while one of its own overlays is open, so a key
 * pressed in a nested dialog never switches the record underneath it).
 */
import { useEffect } from 'react';
import {
  isTypingTarget,
  navKeyDirection,
  type DetailNavigation,
} from '../components/portal/detailNavigation';

export function useDetailNavKeys(
  navigation: DetailNavigation | undefined,
  enabled: boolean,
): void {
  const goNext = navigation?.goNext;
  const goPrev = navigation?.goPrev;
  const hasNext = navigation?.hasNext ?? false;
  const hasPrev = navigation?.hasPrev ?? false;

  useEffect(() => {
    if (!enabled || !goNext || !goPrev) return;
    const handler = (e: KeyboardEvent) => {
      if (e.defaultPrevented) return;
      const direction = navKeyDirection(e);
      if (!direction) return;
      if (isTypingTarget(e.target)) return;
      if (direction === 'next') {
        if (!hasNext) return;
        e.preventDefault();
        goNext('keyboard');
      } else {
        if (!hasPrev) return;
        e.preventDefault();
        goPrev('keyboard');
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [enabled, goNext, goPrev, hasNext, hasPrev]);
}
